<template lang="pug">
#careimage-multiple-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 介護記録一括登録

    .box-body
      // careimage-date
      .careimage-date.d-flex
        .date-area
          .left-side.d-flex
            label 記録日：{{ _date }}
            span ※1枚目～4枚目のファイルはドラッグ＆ドロップで並び替えが可能です

      // error messages
      .error-messages(v-for="error in errors.collect('careimage-multiple-register-errors')")
        label.error-message {{ error }}
        br
      // register careimage table
      ResidentsCareimageTable(
        ref="table"
        v-model="residents"
        :date="careimageDate"
        :existFamily="_existFamily"
      )
      .register-footer
        .buttons
          button.btn.register-btn(@click="showConfirmModal()" type="button") 登録
          button.btn.cancel-btn(@click="gotoCareimageList()" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="介護記録を登録しますか？" @ok="registerCareimage")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoCareimageList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentModel from "@/commons/models/Resident";
import CareimageModel from "@/commons/models/Careimage";
import CareimagesService from "@/commons/services/Careimages";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import CareimageModal from "@/pc/components/modals/CareimageModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ResidentsCareimageTable from "@/pc/containers/careimages/ResidentsCareimageTable.vue";

/**
 * 介護記録一括登録画面コンポーネント
 */
@Component({
  components: {
    ResidentsCareimageTable,
    CareimageModal,
    OkCancelModal,
    OkModal,
    PageLoading
  },
  $_veeValidate: { validator: "new" }
})
export default class CareimageMultipleRegister extends Vue {
  /** 介護記録サービス */
  @Inject("CareimagesService") careimagesService!: CareimagesService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** 測定日 */
  careimageDate: number = 0;
  /** 利用者 ＋ 1件の介護記録 */
  residents: Array<ResidentCareimageModel> = [];

  /**
   * vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    const date: number = Number(this.$route.params.date);
    this.careimageDate = date;

    return;
  }

  /**
   * 登録確認モーダルを表示する
   * 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showRegisterModal = true;
    }

    return;
  }

  /**
   * 介護記録登録
   */
  public async registerCareimage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    /**
     * 利用者毎に介護記録を登録する
     * 時間がかかるため並列で行う
     */
    const results: Array<any> = await Promise.all(
      this.residents.map(async (resident: ResidentCareimageModel) => {
        try {
          // 変更していない利用者の介護記録は何もしない
          if (!resident.edited) {
            return;
          }

          // 画像が0件なら介護記録を削除する
          if (resident.files.length < 1) {
            await this.careimagesService.delete(userStore, resident.id, this.careimageDate);
            return;
          }

          // 介護記録を登録する(修正の場合でもPOSTで登録しなおし)
          let urls: Array<S3LocationResponse> = await this.createUploadUrls(resident.id, resident.files);
          for (let i = 0; i < urls.length; i++) {
            await this.s3UploadsService.uploadBase64File(urls[i], resident.files[i].dataUrl);
          }
          const registerCareimage: CareimageModel = this.createRegisterParam(resident, urls);
          await this.careimagesService.post(userStore, resident.id, registerCareimage);
        } catch (error) {
          // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
          return error;
        }
      })
    );

    // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
    for (const result of results) {
      if (result) {
        // エラーハンドル処理を実行する
        errorHandle(result, this);
      }
    }

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(residentId: number, files: Array<any>): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");

      let extension: string = _extensions[_extensions.length - 1];
      if (extension.match(/jpg/)) {
        extension = "jpeg";
      }
      // 拡張子がpdfの場合は実際にはjpegに変換されているのでpdfに直す
      else if (extension.match(/pdf/)) {
        extension = "jpeg";
      }

      extensions.push("." + extension);
    }
    const requestBody: S3LocationRequest = {
      type: S3LocationType.CAREIMAGE,
      count: files.length,
      extensions: extensions,
      params: { residentId: residentId, datetime: this.careimageDate }
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * 介護記録登録パラメータを作成する
   *
   * - 画像が存在する場合、登録パラメータを作成
   * - 画像が存在するが更新していない場合、登録パラメータを作成しない
   * - 画像が存在しないが更新されている場合、登録パラメータを作成する
   * - 画像が存在しないが更新されていない場合、登録パラメータを作成しない
   *
   * @param パラメータ作成元となる介護記録
   */
  public createRegisterParam(resident: ResidentCareimageModel, urls: Array<S3LocationResponse>): CareimageModel {
    let imageUrls: Array<string> = [];
    for (const url of urls) {
      const preUrl: string = url.url.endsWith("/") ? url.url : `${url.url}/`;
      const imageUrl: string = `${preUrl}${url.fields["key"]}`;
      imageUrls.push(imageUrl);
    }

    const registerCareimage: CareimageModel = {
      datetime: this.careimageDate,
      isPublic: false,
      deleteFlag: false,
      facilityId: userStore.loginUser.facilityId,
      imageUrls: imageUrls,
      residentId: resident.id,
      residentName: resident.name,
      residentRoom: resident.room,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId
    };

    return registerCareimage;
  }

  /**
   * 介護記録一覧に遷移する
   */
  public gotoCareimageList(): void {
    this.$router.push(PcUrl.CareimageList);

    return;
  }

  /**
   * 測定日を取得する
   */
  get _date(): string {
    return this.$dateUtil.convertNumberToString(this.careimageDate, "yyyy年mm月dd日", false);
  }

  /**
   * 家族ユーザーが登録されている利用者に限定するかの条件を取得する
   */
  get _existFamily(): boolean {
    const existFamily: string = this.$route.params.existfamily;
    if (existFamily == "true") {
      return true;
    } else {
      return false;
    }
  }
}

/**
 * ### 介護記録にひもづく利用者テーブルのModel
 *
 * 利用者情報 ＋ 1件の介護記録
 */
export interface ResidentCareimageModel extends ResidentModel {
  /** 利用者にひもづく介護記録 */
  careimage: CareimageModel;
  /** 変更したか否か */
  edited: boolean;
  /** 登録する介護記録ファイル */
  files: Array<any>;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#careimage-multiple-register {
  max-width: 1771px;
  margin-left: auto;
  margin-right: auto;

  .box-body {
    .careimage-date {
      // box-bodyのpaddingに合わせて、下に10px余白を取る
      margin-bottom: 10px;

      .date-area {
        label {
          @include bold-font();
          // BOOTSTRAPのスタイルを上書き
          margin: 0 8px 0 0 !important;
        }
      }
    }
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
